import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { Property } from "models/properties";
import { Product } from "interfaces/products";
import { getPropertyDetailsInfo } from "state/modules/properties";
import ProductsClient from "services/api/products";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_BOLD } from "styles/typography";

const Container = styled.div`
	padding: 15px 70px 0;

	@media (max-width: 1200px) {
		padding: 15px 50px 0;
	}

	@media (max-width: 768px) {
		padding: 15px 15px 0;
	}

	@media (max-width: 480px) {
		padding: 15px 10px 0;
	}
`;

const Content = styled.div`
	border-radius: 8px;
	background: ${MAIN_THEME.DARK_BG_SECONDARY};
	padding: 20px;
	margin-bottom: 20px;
	max-width: 600px;
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
`;

const Title = styled.p`
	font-family: ${FONT_FAMILY_BOLD};
	font-style: normal;
	font-weight: bold;
	font-size: 16px;
	line-height: 33px;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	color: ${MAIN_THEME.TEXT_PRIMARY};
	margin-bottom: 20px;
`;

const SettingRow = styled.div`
	display: flex;
	align-items: flex-start;
	margin-bottom: 16px;

	&:last-child {
		margin-bottom: 0;
	}
`;

const Label = styled.span`
	font-family: ${FONT_FAMILY_BOLD};
	font-size: 14px;
	color: ${MAIN_THEME.TEXT_SECONDARY};
	min-width: 140px;
	margin-right: 16px;
`;

const Value = styled.span`
	font-size: 14px;
	color: ${MAIN_THEME.TEXT_PRIMARY};
`;

const SubProductsList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const SubProductItem = styled.div`
	padding: 12px;
	background: ${MAIN_THEME.DARK_BG};
	border-radius: 6px;
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
`;

const SubProductName = styled.span`
	font-size: 14px;
	color: ${MAIN_THEME.TEXT_PRIMARY};
	font-weight: 500;
`;

const SubProductDetails = styled.div`
	display: flex;
	gap: 16px;
	margin-top: 6px;
	font-size: 12px;
	color: ${MAIN_THEME.TEXT_SECONDARY};
`;

const EmptyState = styled.div`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 14px;
	font-style: italic;
`;

const LoadingState = styled.div`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 14px;
`;

const PropertySettings = (): JSX.Element => {
	const [welcomePackProduct, setWelcomePackProduct] = useState<Product | null>(
		null
	);
	const [isLoading, setIsLoading] = useState(false);

	const propertyDetails = useSelector(getPropertyDetailsInfo) as Property;

	const welcomePackId = propertyDetails?.hotelise?.propertySettings?.welcomePackId;

	useEffect(() => {
		const fetchWelcomePack = async () => {
			if (!welcomePackId) {
				setWelcomePackProduct(null);
				return;
			}

			try {
				setIsLoading(true);
				const response = await ProductsClient.getProductById(welcomePackId);
				setWelcomePackProduct(response.data?.content || null);
			} catch (error) {
				console.error("Failed to fetch welcome pack:", error);
				setWelcomePackProduct(null);
			} finally {
				setIsLoading(false);
			}
		};

		fetchWelcomePack();
	}, [welcomePackId]);

	const renderWelcomePackContent = () => {
		if (isLoading) {
			return <LoadingState>Loading welcome pack details...</LoadingState>;
		}

		if (!welcomePackId) {
			return <EmptyState>No welcome pack assigned to this property</EmptyState>;
		}

		if (!welcomePackProduct) {
			return <EmptyState>Welcome pack not found</EmptyState>;
		}

		return (
			<>
				<SettingRow>
					<Label>Name:</Label>
					<Value>{welcomePackProduct.name}</Value>
				</SettingRow>
				<SettingRow>
					<Label>Product Code:</Label>
					<Value>{welcomePackProduct.productCode}</Value>
				</SettingRow>
				<SettingRow>
					<Label>Supplier:</Label>
					<Value>{welcomePackProduct.supplier}</Value>
				</SettingRow>
				<SettingRow>
					<Label>Price:</Label>
					<Value>
						{welcomePackProduct.price} {welcomePackProduct.currency}
					</Value>
				</SettingRow>
				{welcomePackProduct.description && (
					<SettingRow>
						<Label>Description:</Label>
						<Value>{welcomePackProduct.description}</Value>
					</SettingRow>
				)}
				{welcomePackProduct.subProducts &&
					welcomePackProduct.subProducts.length > 0 && (
						<SettingRow>
							<Label>Items:</Label>
							<SubProductsList>
								{welcomePackProduct.subProducts.map((subProduct) => (
									<SubProductItem key={subProduct.id}>
										<SubProductName>{subProduct.name}</SubProductName>
										<SubProductDetails>
											<span>Code: {subProduct.productCode}</span>
											<span>Qty: {subProduct.quantity}</span>
											<span>
												{subProduct.price} {subProduct.currency}
											</span>
										</SubProductDetails>
									</SubProductItem>
								))}
							</SubProductsList>
						</SettingRow>
					)}
			</>
		);
	};

	return (
		<Container>
			<Content>
				<Title>Welcome Pack</Title>
				{renderWelcomePackContent()}
			</Content>
		</Container>
	);
};

export default PropertySettings;
